import { useEffect, useState } from 'react'
import { Icon } from '../../../../public/icons/Icon.tsx'
import AnchoredPopover from '../../../components/AnchoredPopover'
import Sheet from '../../../components/Sheet'
import { Coin } from '../../../icons/Coin'
import { useAuth } from '../../../context/AuthContext'
import { useAuthLink } from '../../../hooks/useAuthModal'
import { buildIdeaContext, ideaStage, type IdeaItem, type IdeaSettings, type IdeaVariant } from '../../../api/promptIdea'
import type { Model } from '../../../data/models'

/**
 * IdeaWizardSheet — «Придумать идею»: трёхшаговый мастер (Тема → Идея → Промпт)
 * на дешёвой LLM. Темы заточены под выбранную модель и сезон; итог — готовый
 * промпт + рекомендованные настройки, которые применяются к виджету.
 *
 * Экономика: N бесплатных циклов в день (перегенерации внутри цикла бесплатны),
 * дальше платный цикл с ЯВНЫМ подтверждением цены до списания. Гости видят
 * мягкий гейт на вход. Десктоп — поповер у кнопки, мобила — bottom-sheet.
 */
interface Props {
	open: boolean
	onClose: () => void
	anchorEl: HTMLElement | null
	model: Model
	onInsert: (prompt: string, settings: IdeaSettings) => void
	preferPlacement?: 'above' | 'below'
}

type Step = 'themes' | 'ideas' | 'prompt'

// Фирменная иконка фичи — та же «искра», что на кнопке «Придумать идею»
// (узнаваемость: один функционал = одна иконка; прицел занят обучалкой).
const SPARK = <Icon id="gen-idea" size={15} />

export default function IdeaWizardSheet({ open, onClose, anchorEl, model, onInsert, preferPlacement }: Props) {
	// refreshUser — сразу обновить баланс в шапке после платного цикла/шага
	// (иначе списание «не видно» до следующего действия на сайте).
	const { user, refreshUser } = useAuth()
	// «Войти и попробовать» — окном поверх страницы; мастер закрывается, после входа открывается заново
	const authLink = useAuthLink()
	const [isMobile, setIsMobile] = useState(
		() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
	)
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 640px)')
		const fn = (e: MediaQueryListEvent) => setIsMobile(e.matches)
		mq.addEventListener('change', fn)
		return () => mq.removeEventListener('change', fn)
	}, [])

	const [step, setStep] = useState<Step>('themes')
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [sessionId, setSessionId] = useState<string | null>(null)
	const [themes, setThemes] = useState<string[]>([])
	const [theme, setTheme] = useState<string>('')
	const [customTheme, setCustomTheme] = useState('')
	const [ideas, setIdeas] = useState<IdeaItem[]>([])
	const [result, setResult] = useState<{ prompt: string; settings: IdeaSettings } | null>(null)
	// Шаг 3 может отдать несколько вариантов за один вызов (настройка админки);
	// result всегда = АКТИВНЫЙ вариант, чтобы вставка/переходы не менялись.
	const [variants, setVariants] = useState<IdeaVariant[]>([])
	const [variantIdx, setVariantIdx] = useState(0)
	const [freeLeft, setFreeLeft] = useState<number | null>(null)
	const [freeLimit, setFreeLimit] = useState<number | null>(null)
	// Бесплатные попытки на шаг внутри цикла (по 3; null = шаг ещё не звался).
	// После нуля шаг НЕ блокируется — перегенерации платные поштучно (stepPay).
	const [attempts, setAttempts] = useState<{ themes: number | null; ideas: number | null; prompt: number | null }>({ themes: null, ideas: null, prompt: null })
	const STEP_LIMIT_MSG = 'Лимит вызовов этого цикла исчерпан — начни новый цикл.'
	const [payPrice, setPayPrice] = useState<number | null>(null) // экран подтверждения оплаты ЦИКЛА
	// Подтверждение поштучной оплаты перегенерации шага: цена + повтор запроса.
	const [stepPay, setStepPay] = useState<{ price: number; run: () => void } | null>(null)
	// Одноразовое согласие на платные перегенерации В ЭТОМ ЦИКЛЕ: после первого
	// «Продолжить за N» плашка больше не показывается — цена уходит прямо на
	// кнопки ↻ (паттерн главной кнопки генерации: цена на борту, клик =
	// списание). Действия БЕЗ ценника (чипы тем/идей) по-прежнему идут через
	// плашку — молча списывать с контрола без цены нечестно.
	const [stepPaidPrice, setStepPaidPrice] = useState<number | null>(null)
	// Цена платной перегенерации шага (из стартового themes-ответа): кнопки
	// показывают «N ✦» сразу, как только бесплатные попытки исчерпаны.
	const [stepPrice, setStepPrice] = useState<number | null>(null)

	// Единый вид кнопки перегенерации: free — «↻ N», платно — ТОЛЬКО цена и
	// токен (одно значение на кнопке, без смешения символов).
	const regenContent = (left: number | null) =>
		left === 0 ? (
			<>
				{stepPaidPrice ?? stepPrice ?? 1} <Coin width={13} height={13} />
			</>
		) : (
			<>↻{left != null ? ` ${left}` : ''}</>
		)
	const regenTitle = (left: number | null, what: string) =>
		left === 0
			? `Платная перегенерация — ${stepPaidPrice ?? stepPrice ?? 1} за попытку`
			: `${what}${left != null ? ` (осталось ${left})` : ''}`
	const [paidCycle, setPaidCycle] = useState(false)
	// Ключ последнего старта — цикл перезапускается при повторном открытии/смене модели.
	const [startedKey, setStartedKey] = useState<string | null>(null)
	const wizardKey = `${model.id}`

	const ctx = buildIdeaContext(model)

	const startCycle = async (acceptCharge: boolean) => {
		setLoading(true)
		setError(null)
		setPayPrice(null)
		setStepPay(null)
		setStepPaidPrice(null)
		setStep('themes')
		setTheme('')
		setIdeas([])
		setResult(null)
		setVariants([])
		setVariantIdx(0)
		setSessionId(null)
		setPaidCycle(false)
		setAttempts({ themes: null, ideas: null, prompt: null })
		try {
			const res = await ideaStage({ stage: 'themes', context: ctx, acceptCharge })
			if (res.need_payment) {
				setPayPrice(res.price_credits ?? null)
				setFreeLeft(0)
				if (res.free_limit != null) setFreeLimit(res.free_limit)
				return
			}
			if (!res.ok) {
				setError(res.error === 'insufficient_credits'
					? `Недостаточно токенов — нужен ${res.price_credits ?? ''} за цикл. Пополни баланс и возвращайся.`
					: res.error === 'rate_limited'
						? 'Слишком часто — подожди минуту и попробуй снова.'
						: 'Не получилось придумать темы. Попробуй ещё раз.')
				return
			}
			setSessionId(res.session_id || null)
			setThemes(res.themes || [])
			if (res.attempts_left != null) setAttempts((p) => ({ ...p, themes: res.attempts_left! }))
			if (res.free_left != null) setFreeLeft(res.free_left)
			if (res.free_limit != null) setFreeLimit(res.free_limit)
			if (res.step_price_credits != null) setStepPrice(res.step_price_credits)
			if (acceptCharge) {
				setPaidCycle(true)
				void refreshUser()
			}
		} catch {
			setError('Сервис идей недоступен. Попробуй позже.')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (!open || !user) return
		if (startedKey === wizardKey && (themes.length > 0 || payPrice !== null)) return
		setStartedKey(wizardKey)
		void startCycle(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, user, wizardKey])

	const regenThemes = async (acceptCharge = false) => {
		if (!sessionId) return void startCycle(false)
		setLoading(true)
		setError(null)
		setStepPay(null)
		try {
			const res = await ideaStage({ stage: 'themes', sessionId, context: ctx, acceptCharge })
			if (res.ok) {
				setThemes(res.themes || [])
				if (res.attempts_left != null) setAttempts((p) => ({ ...p, themes: res.attempts_left! }))
				if (acceptCharge) void refreshUser()
			} else if (res.need_payment && res.scope === 'step') {
				setAttempts((p) => ({ ...p, themes: 0 }))
				if (res.price_credits != null) setStepPrice(res.price_credits)
				setStepPay({ price: res.price_credits ?? 1, run: () => void regenThemes(true) })
			} else if (res.error === 'insufficient_credits') {
				setError(`Недостаточно токенов — перегенерация стоит ${res.price_credits ?? 1}. Пополни баланс.`)
			} else if (res.error === 'step_limit') {
				setAttempts((p) => ({ ...p, themes: 0 }))
				setError(STEP_LIMIT_MSG)
			} else if (res.error === 'rate_limited') {
				setError('Слишком часто — подожди минуту и попробуй снова.')
			} else {
				setError('Не получилось, попробуй ещё раз.')
			}
		} catch {
			setError('Сервис идей недоступен.')
		} finally {
			setLoading(false)
		}
	}

	const loadIdeas = async (chosenTheme: string, acceptCharge = false) => {
		if (!sessionId) return
		setTheme(chosenTheme)
		setStep('ideas')
		setLoading(true)
		setError(null)
		setStepPay(null)
		try {
			const res = await ideaStage({ stage: 'ideas', sessionId, context: ctx, theme: chosenTheme, acceptCharge })
			if (res.ok) {
				setIdeas(res.ideas || [])
				if (res.attempts_left != null) setAttempts((p) => ({ ...p, ideas: res.attempts_left! }))
				if (acceptCharge) void refreshUser()
			} else if (res.need_payment && res.scope === 'step') {
				setAttempts((p) => ({ ...p, ideas: 0 }))
				if (res.price_credits != null) setStepPrice(res.price_credits)
				setStepPay({ price: res.price_credits ?? 1, run: () => void loadIdeas(chosenTheme, true) })
			} else if (res.error === 'insufficient_credits') {
				setError(`Недостаточно токенов — перегенерация стоит ${res.price_credits ?? 1}. Пополни баланс.`)
				if (ideas.length === 0) setStep('themes')
			} else if (res.error === 'step_limit') {
				setAttempts((p) => ({ ...p, ideas: 0 }))
				setError(STEP_LIMIT_MSG)
				if (ideas.length === 0) setStep('themes')
			} else if (res.error === 'rate_limited') {
				setError('Слишком часто — подожди минуту и попробуй снова.')
			} else {
				setError('Не получилось, попробуй ещё раз.')
			}
		} catch {
			setError('Сервис идей недоступен.')
		} finally {
			setLoading(false)
		}
	}

	const loadPrompt = async (chosenIdea: IdeaItem | null, acceptCharge = false) => {
		if (!sessionId) return
		setStep('prompt')
		setLoading(true)
		setError(null)
		setStepPay(null)
		try {
			const res = await ideaStage({ stage: 'prompt', sessionId, context: ctx, theme, idea: chosenIdea, acceptCharge })
			if (res.ok && res.prompt) {
				const vars: IdeaVariant[] = res.variants?.length
					? res.variants
					: [{ prompt: res.prompt, settings: res.settings || { aspect_ratio: null, duration: null, sound: null } }]
				setVariants(vars)
				setVariantIdx(0)
				setResult({ prompt: vars[0].prompt, settings: vars[0].settings })
				if (res.attempts_left != null) setAttempts((p) => ({ ...p, prompt: res.attempts_left! }))
				if (acceptCharge) void refreshUser()
			} else if (res.need_payment && res.scope === 'step') {
				setAttempts((p) => ({ ...p, prompt: 0 }))
				if (res.price_credits != null) setStepPrice(res.price_credits)
				setStepPay({ price: res.price_credits ?? 1, run: () => void loadPrompt(chosenIdea, true) })
			} else if (res.error === 'insufficient_credits') {
				setError(`Недостаточно токенов — перегенерация стоит ${res.price_credits ?? 1}. Пополни баланс.`)
				if (!result) setStep(ideas.length > 0 ? 'ideas' : 'themes')
			} else if (res.error === 'step_limit') {
				setAttempts((p) => ({ ...p, prompt: 0 }))
				setError(STEP_LIMIT_MSG)
				if (!result) setStep(ideas.length > 0 ? 'ideas' : 'themes')
			} else if (res.error === 'rate_limited') {
				setError('Слишком часто — подожди минуту и попробуй снова.')
			} else {
				setError('Не получилось, попробуй ещё раз.')
			}
		} catch {
			setError('Сервис идей недоступен.')
		} finally {
			setLoading(false)
		}
	}

	// Отказ от платной перегенерации: остаёмся на шаге с готовым контентом,
	// а если его нет (платный вход в шаг) — мягко возвращаем на предыдущий.
	const declineStepPay = () => {
		setStepPay(null)
		if (step === 'ideas' && ideas.length === 0) setStep('themes')
		if (step === 'prompt' && !result) setStep(ideas.length > 0 ? 'ideas' : 'themes')
	}

	const crumb = (target: Step, label: string, enabled: boolean) => (
		<button
			type="button"
			className={`idea-wizard__crumb${step === target ? ' is-active' : ''}`}
			disabled={!enabled || loading}
			onClick={() => {
				if (target === 'themes') { setStep('themes'); setError(null) }
				if (target === 'ideas' && ideas.length > 0) { setStep('ideas'); setError(null) }
			}}
		>
			{label}
		</button>
	)

	const skeleton = (
		<div className="idea-wizard__skeleton" aria-hidden="true"><span /><span /><span /></div>
	)

	const settingsChips = (s: IdeaSettings) => {
		const chips: string[] = []
		if (s.aspect_ratio) chips.push(s.aspect_ratio)
		if (s.duration) chips.push(`${s.duration} сек`)
		if (s.sound != null) chips.push(s.sound ? 'звук: вкл' : 'звук: выкл')
		return chips
	}

	const content = (
		<div className="idea-wizard">
			<div className="idea-wizard__head">
				<span className="idea-wizard__spark" aria-hidden="true">{SPARK}</span>
				<span className="idea-wizard__title">Придумать идею</span>
				{user && freeLeft != null && freeLimit != null && (
					<span className="idea-wizard__free" title="Бесплатные циклы на сегодня. Дальше — платно, с предупреждением.">
						{paidCycle ? 'Платный цикл' : `Бесплатно: ${freeLeft}/${freeLimit}`}
					</span>
				)}
				{!isMobile && (
					<button type="button" className="idea-wizard__close" onClick={onClose} aria-label="Закрыть">
						<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
							<path d="M18 6 6 18M6 6l12 12" />
						</svg>
					</button>
				)}
			</div>

			{!user ? (
				<div className="idea-wizard__gate">
					<p className="idea-wizard__gate-title">Идеи под выбранную нейросеть</p>
					<p className="idea-wizard__gate-text">
						Мастер подберёт тему, сюжет и готовый промпт за три клика.
						После входа — {freeLimit ?? 3} идеи в день бесплатно.
					</p>
					<a {...authLink('/register', { onOpen: onClose })} className="idea-wizard__gate-btn">Войти и попробовать</a>
				</div>
			) : payPrice !== null ? (
				<div className="idea-wizard__pay">
					<p className="idea-wizard__pay-title">Бесплатные идеи на сегодня закончились</p>
					<p className="idea-wizard__pay-text">
						Мы честно предупреждаем: следующий цикл (темы → идея → промпт, включая
						перегенерации) — платный. Завтра снова будут бесплатные.
					</p>
					<button type="button" className="idea-wizard__pay-btn" disabled={loading} onClick={() => void startCycle(true)}>
						Продолжить за {payPrice} <Coin width={13} height={13} />
					</button>
				</div>
			) : (
				<>
					<div className="idea-wizard__crumbs">
						{crumb('themes', '1 Тема', true)}
						<span className="idea-wizard__crumb-sep" aria-hidden="true">›</span>
						{crumb('ideas', '2 Идея', ideas.length > 0)}
						<span className="idea-wizard__crumb-sep" aria-hidden="true">›</span>
						<span className={`idea-wizard__crumb${step === 'prompt' ? ' is-active' : ''}`}>3 Промпт</span>
					</div>

					<div className="idea-wizard__body">
						{stepPay && (
							<div className="idea-wizard__pay idea-wizard__pay--step">
								<p className="idea-wizard__pay-title">Бесплатные попытки шага закончились</p>
								<p className="idea-wizard__pay-text">
									Дальше каждая перегенерация — {stepPay.price} <Coin width={13} height={13} />.
									Или просто выбери из уже готовых вариантов.
								</p>
								<div className="idea-wizard__pay-row">
									<button
										type="button"
										className="idea-wizard__pay-btn"
										disabled={loading}
										onClick={() => {
											const run = stepPay.run
											// Согласие запоминаем на цикл: дальше без плашки,
											// цена видна прямо на кнопках перегенерации.
											setStepPaidPrice(stepPay.price)
											setStepPay(null)
											run()
										}}
									>
										Продолжить за {stepPay.price} <Coin width={13} height={13} />
									</button>
									<button type="button" className="idea-wizard__pay-skip" disabled={loading} onClick={declineStepPay}>
										Не сейчас
									</button>
								</div>
							</div>
						)}
						{error && <div className="idea-wizard__error">{error}</div>}

						{step === 'themes' && (
							<>
								<p className="idea-wizard__label">О чём будет генерация? Темы под {model.name}:</p>
								{loading ? skeleton : (
									<div className="idea-wizard__chips">
										{themes.map((t) => (
											<button key={t} type="button" className="idea-wizard__chip" onClick={() => void loadIdeas(t)}>
												{t}
											</button>
										))}
									</div>
								)}
								<div className="idea-wizard__row">
									<input
										value={customTheme}
										onChange={(e) => setCustomTheme(e.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter' && customTheme.trim()) void loadIdeas(customTheme.trim()) }}
										placeholder="или впиши свою тему…"
										className="idea-wizard__input"
										disabled={loading}
									/>
									{customTheme.trim() ? (
										<button type="button" className="idea-wizard__dice" disabled={loading} onClick={() => void loadIdeas(customTheme.trim())}>
											→
										</button>
									) : (
										<button
											type="button"
											className="idea-wizard__dice"
											disabled={loading}
											onClick={() => void regenThemes(attempts.themes === 0 && stepPaidPrice != null)}
											title={regenTitle(attempts.themes, 'Другие темы')}
										>
											{regenContent(attempts.themes)}
										</button>
									)}
								</div>
							</>
						)}

						{step === 'ideas' && (
							<>
								<p className="idea-wizard__label">Тема: «{theme}». Выбери сюжет:</p>
								{loading ? skeleton : (
									<>
										{ideas.map((i) => (
											<button key={i.title} type="button" className="idea-wizard__idea" onClick={() => void loadPrompt(i)}>
												<span className="idea-wizard__idea-title">{i.title}</span>
												<span className="idea-wizard__idea-desc">{i.desc}</span>
											</button>
										))}
										<div className="idea-wizard__row">
											<button
												type="button"
												className="idea-wizard__fast"
												disabled={loading}
												onClick={() => void loadPrompt(null, attempts.prompt === 0 && stepPaidPrice != null)}
											>
												{attempts.prompt === 0 && stepPaidPrice != null
													? <>⚡ Сразу промпт по теме · {stepPaidPrice} <Coin width={13} height={13} /></>
													: '⚡ Сразу промпт по теме — пропустить шаг'}
											</button>
											<button
												type="button"
												className="idea-wizard__dice"
												disabled={loading}
												onClick={() => void loadIdeas(theme, attempts.ideas === 0 && stepPaidPrice != null)}
												title={regenTitle(attempts.ideas, 'Ещё 3 идеи')}
											>
												{regenContent(attempts.ideas)}
											</button>
										</div>
									</>
								)}
							</>
						)}

						{step === 'prompt' && (
							<>
								{loading || !result ? skeleton : (
									<>
										{variants.length > 1 && (
											<div className="idea-wizard__variants" role="tablist" aria-label="Варианты промпта">
												{variants.map((v, i) => (
													<button
														key={i}
														type="button"
														role="tab"
														aria-selected={i === variantIdx}
														className={`idea-wizard__variant${i === variantIdx ? ' is-active' : ''}`}
														onClick={() => { setVariantIdx(i); setResult({ prompt: v.prompt, settings: v.settings }) }}
													>
														Вариант {i + 1}
													</button>
												))}
											</div>
										)}
										<div className="idea-wizard__prompt">{result.prompt}</div>
										{settingsChips(result.settings).length > 0 && (
											<div className="idea-wizard__pset">
												{settingsChips(result.settings).map((c) => <span key={c}>{c}</span>)}
												<span className="idea-wizard__pset-note">применятся к виджету</span>
											</div>
										)}
										<div className="idea-wizard__cta">
											<button
												type="button"
												className="idea-wizard__insert"
												onClick={() => { onInsert(result.prompt, result.settings); onClose() }}
											>
												Вставить в виджет
											</button>
											<button
												type="button"
												className="idea-wizard__dice"
												disabled={loading}
												onClick={() => void loadPrompt(null, attempts.prompt === 0 && stepPaidPrice != null)}
												title={regenTitle(attempts.prompt, 'Другой вариант')}
											>
												{regenContent(attempts.prompt)}
											</button>
										</div>
									</>
								)}
							</>
						)}
					</div>
				</>
			)}
		</div>
	)

	if (isMobile) {
		return (
			<Sheet open={open} onClose={onClose} variant="bottom" showHandle contentClassName="idea-wizard-sheet">
				{content}
			</Sheet>
		)
	}
	return (
		<AnchoredPopover open={open} onClose={onClose} anchorEl={anchorEl} width={380} align="center" className="idea-wizard-popover" preferDirection={preferPlacement}>
			{content}
		</AnchoredPopover>
	)
}
